import { User, LogOut, Settings, Shield, UserCog } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../store/useStore';

export const UserProfile = () => {
  const { user, profile, signOut } = useAuth();
  const { addNotification } = useNotifications();

  const handleSignOut = async () => {
    try {
      await signOut();
      addNotification({
        type: 'success',
        title: 'Logout effettuato',
        message: 'Sei stato disconnesso con successo'
      });
    } catch (error) {
      console.error('Logout error:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Impossibile effettuare il logout'
      });
    }
  };

  const getRoleLabel = (role: string) => {
    const labels = {
      admin: 'Amministratore',
      commerciale: 'Commerciale',
      amministrazione: 'Amministrazione',
      lettore: 'Lettore',
      production: 'Produzione',
      sales: 'Vendite',
      customer_user: 'Cliente',
      lab: 'Laboratorio',
      warehouse: 'Magazzino'
    };
    return labels[role as keyof typeof labels] || role;
  };

  const getRoleColor = (role: string) => {
    const colors = {
      admin: 'text-red-600',
      commerciale: 'text-primary-600',
      amministrazione: 'text-violet-600',
      lettore: 'text-gray-600',
      production: 'text-orange-600',
      sales: 'text-lime-600',
      customer_user: 'text-yellow-600',
      lab: 'text-pink-600',
      warehouse: 'text-emerald-600'
    };
    return colors[role as keyof typeof colors] || 'text-gray-600';
  };

  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  const getAvatarColor = (email: string) => {
    // Generate a consistent color based on email
    // Erano otto tinte diverse, tre delle quali azzurre: sostituite con
    // altrettante non azzurre, altrimenti tre utenti su otto avrebbero
    // ricevuto lo stesso identico avatar.
    const colors = [
      'bg-red-500',
      'bg-violet-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-lime-500',
      'bg-amber-500'
    ];
    const index = email.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (!user) {
    return (
      <Button variant="outline" asChild>
        <Link to="/auth/login">
          <User className="w-4 h-4 mr-2" />
          Accedi
        </Link>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`h-10 w-10 rounded-full ${getAvatarColor(user.email || '')} hover:opacity-80 text-white font-semibold text-lg transition-all duration-200 hover:scale-105 shadow-md`}
        >
          {getInitials(user.email || '')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] max-w-xs sm:w-64">
        <div className="px-3 py-2">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex-shrink-0 ${getAvatarColor(user.email || '')} rounded-full flex items-center justify-center text-white font-semibold`}>
              {getInitials(user.email || '')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {profile?.full_name || user.email}
              </p>
              <p className="text-sm text-gray-500 truncate">{user.email}</p>
              {profile?.role && (
                <div className="flex items-center space-x-1 mt-1">
                  <Shield className="w-3 h-3 text-gray-400" />
                  <span className={`text-xs font-medium ${getRoleColor(profile.role)}`}>
                    {getRoleLabel(profile.role)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Admin-only User Management */}
        {profile?.role === 'admin' && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/user-management" className="flex items-center">
                <UserCog className="w-4 h-4 mr-2" />
                Gestione Utenti
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Per tutti: e' da li' che ognuno cambia la propria password. */}
        <DropdownMenuItem asChild>
          <Link to="/impostazioni" className="flex items-center">
            <Settings className="w-4 h-4 mr-2" />
            Impostazioni
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleSignOut} className="text-red-600">
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>

    </DropdownMenu>
  );
};
export default UserProfile;